import { displayToInternal, internalToDisplay, parseStation, type Alignment } from '@/core/alignment';
import type { StartClearanceInput } from '@/graphql/graphql';
import { toAlignmentInput, type AlignmentInputPayload } from '@/lib/stationVolumes/alignmentInput';
import { profileError, validateProfileVertices } from './profile';
import type { ClearanceVertex } from './types';

export const DEFAULT_STATION_INTERVAL = 20;
export const INTERVAL_PRESETS = [5, 10, 20, 25, 50] as const;
/** Same cap as the resolver (MAX_STATION_BINS_CLEARANCE) and the worker (MAX_BINS). */
export const MAX_STATION_BINS = 20_000;
/** Above this the dialog warns; it does not block. */
export const WARN_BINS = 2_000;
/**
 * Mirrors of `clearanceValidation.ts`'s `MAX_PROFILE_NAME_CHARS` /
 * `MAX_ATTRIBUTION_CHARS` (the attribution one matches
 * `clearance_profiles.sourceAttribution`'s varchar length). They cannot be
 * imported across the app/server boundary, so they are duplicated here on the
 * same "change one, change both" terms as `profile.ts`'s rules — without them
 * an over-long name arms the Run button and then fails server-side in a toast,
 * which is the round-trip the client-side mirror exists to prevent.
 */
export const MAX_PROFILE_NAME_CHARS = 120;
export const MAX_ATTRIBUTION_CHARS = 300;
/**
 * Finding F-5: `StartClearanceInput.name` (the optional OUTPUT run/layer
 * name, distinct from `profileName` above) had no length rule anywhere —
 * unbounded input riding onto the 8 KB `PAYLOAD_JSON` ECS override
 * (`libs/common/src/ecs.ts`) with no size check, so a long paste failed the
 * run with a raw AWS `InvalidParameterException` instead of a field error.
 * Mirrors `clearanceValidation.ts`'s `MAX_OUTPUT_NAME_CHARS` — same cap as
 * `MAX_PROFILE_NAME_CHARS` for consistency, cannot be imported across the
 * app/server boundary.
 */
export const MAX_OUTPUT_NAME_CHARS = 120;

export type ClearanceFormValues = {
  name: string;
  pointcloudLayerId: string;
  alignmentKey: string;
  profileId: string;
  profileName: string;
  /** Published-standard citation, snapshotted onto the run (ruling K4). */
  profileSourceAttribution: string | null;
  /** True while a shipped standard preset is selected — the editor is locked. */
  profileReadOnly: boolean;
  profileVertices: ClearanceVertex[];
  verticalOffsetM: number;
  stationInterval: number;
  stationFromText: string;
  stationToText: string;
};

export const clearanceDefaults = (): ClearanceFormValues => ({
  name: '',
  pointcloudLayerId: '',
  alignmentKey: '',
  profileId: '',
  profileName: '',
  profileSourceAttribution: null,
  profileReadOnly: false,
  profileVertices: [],
  verticalOffsetM: 0,
  stationInterval: DEFAULT_STATION_INTERVAL,
  stationFromText: '',
  stationToText: '',
});

export type ClearanceBlocking =
  | 'noAlignment'
  | 'profileInvalid'
  /** `profileName` exceeds `MAX_PROFILE_NAME_CHARS`. */
  | 'nameTooLong'
  /**
   * `profileName` IS a shipped standard's name while the geometry is the
   * user's own (finding N1). The editor renames on every geometry edit and
   * refuses to Save under such a name, but a user who draws their polygon and
   * types the name LAST fires no further edit — and Run would then snapshot
   * "TC E-05 Diagram 1" with `sourceAttribution: null`, which is precisely the
   * artefact spec §1 / ruling K4 exist to prevent.
   */
  | 'nameReserved'
  /** `profileSourceAttribution` exceeds `MAX_ATTRIBUTION_CHARS`. */
  | 'attributionTooLong'
  /** `name` (the output run/layer name) exceeds `MAX_OUTPUT_NAME_CHARS`. */
  | 'outputNameTooLong'
  | 'rangeInvalid'
  | 'rangeTooShort'
  | 'tooManyBins'
  | 'intervalOutOfBounds'
  | 'verticalOffsetOutOfBounds';

export interface ClearanceDerived {
  alignment: Alignment | undefined;
  fromInternal: number;
  toInternal: number;
  estimate: { binCount: number; warnBins: boolean };
  profileError: string | null;
  /** The FIRST reason the Run button is disabled, or null. One reason at a
   *  time: a form that lights up four errors at once teaches nothing. */
  blocking: ClearanceBlocking | null;
}

/** Shared empty default, so the common call allocates no Set per render. */
const NO_RESERVED_NAMES: ReadonlySet<string> = new Set();

/**
 * One derivation, used by BOTH the fields and the Run button, so the two can
 * never disagree about what is blocking. The dialog calls this once per
 * render and passes the result down (the same contract
 * `deriveStationVolumes` has with `StationVolumesFields`).
 *
 * No zod shape and no `refineClearance`: the run dialog holds this form in
 * plain `useState` and reads `deriveClearance` directly, so a zod mirror of
 * these same rules would be exported, never imported, and free to drift.
 */
export const deriveClearance = (
  values: ClearanceFormValues,
  alignments: ReadonlyMap<string, Alignment>,
  /**
   * The shipped presets' names as the user sees them — LOCALISED, trimmed and
   * lower-cased. Passed in rather than imported because the names only exist
   * after `t()`, and this module has to stay a pure, testable derivation.
   * `useShippedProfileNames()` (exported by `ClearanceProfileEditor`) is the
   * one place that builds it, so the Save guard and this Run guard can never
   * disagree about what counts as a standard's name.
   */
  reservedProfileNames: ReadonlySet<string> = NO_RESERVED_NAMES,
): ClearanceDerived => {
  const alignment = values.alignmentKey ? alignments.get(values.alignmentKey) : undefined;
  const pErr = profileError(values.profileVertices);

  const length = alignment?.length ?? 0;
  const parseOr = (text: string, fallback: number): number | null => {
    const trimmed = text.trim();
    if (!trimmed) return fallback;
    if (!alignment) return null;
    const display = parseStation(trimmed);
    if (display === null) return null;
    return displayToInternal(alignment, display);
  };

  const fromInternal = alignment ? parseOr(values.stationFromText, 0) : null;
  const toInternal = alignment ? parseOr(values.stationToText, length) : null;

  const interval = values.stationInterval;
  const span = fromInternal !== null && toInternal !== null ? toInternal - fromInternal : 0;
  const binCount = span > 0 && interval > 0 ? Math.max(1, Math.ceil(span / interval - 1e-9)) : 0;

  let blocking: ClearanceBlocking | null = null;
  if (!alignment) blocking = 'noAlignment';
  else if (pErr) blocking = 'profileInvalid';
  else if (values.profileName.trim().length > MAX_PROFILE_NAME_CHARS) blocking = 'nameTooLong';
  else if (
    // Only an EDITED copy is blocked: the pristine shipped preset legitimately
    // runs under its own name WITH its attribution. Detaching/editing clears
    // the attribution, and that combination — a standard's name, no source —
    // is the artefact spec §1 / ruling K4 exist to prevent.
    reservedProfileNames.has(values.profileName.trim().toLowerCase()) &&
    !values.profileSourceAttribution?.trim()
  )
    blocking = 'nameReserved';
  else if ((values.profileSourceAttribution?.trim().length ?? 0) > MAX_ATTRIBUTION_CHARS)
    blocking = 'attributionTooLong';
  else if (values.name.trim().length > MAX_OUTPUT_NAME_CHARS) blocking = 'outputNameTooLong';
  else if (!Number.isFinite(interval) || interval < 1 || interval > 500) blocking = 'intervalOutOfBounds';
  else if (!Number.isFinite(values.verticalOffsetM) || Math.abs(values.verticalOffsetM) > 1000)
    blocking = 'verticalOffsetOutOfBounds';
  else if (
    fromInternal === null ||
    toInternal === null ||
    fromInternal < 0 ||
    toInternal > length + 1e-6 ||
    toInternal <= fromInternal
  )
    blocking = 'rangeInvalid';
  else if (toInternal - fromInternal < interval) blocking = 'rangeTooShort';
  else if (binCount > MAX_STATION_BINS) blocking = 'tooManyBins';

  return {
    alignment,
    fromInternal: fromInternal ?? 0,
    toInternal: toInternal ?? 0,
    estimate: { binCount, warnBins: binCount > WARN_BINS },
    profileError: pErr,
    blocking,
  };
};

export interface ClearancePayload {
  pointcloudLayerId: string;
  alignment: AlignmentInputPayload;
  profile: { name: string; sourceAttribution: string | null; vertices: ClearanceVertex[] };
  verticalOffsetM: number;
  stationInterval: number;
  stationFrom?: number;
  stationTo?: number;
  name?: string;
}

// Compile-time-only check that the payload really is assignable to the
// generated mutation input — the same guard `alignmentInput.ts` puts on
// `AlignmentInputPayload`. `projectId` is intentionally absent from
// `ClearancePayload` (the dialog spreads it in at the call site), so the
// guard tests the intersection. If codegen ever changes `StartClearanceInput`,
// this line fails to compile here rather than in the dialog. Never called.
const _assignableToStartClearanceInput = (p: ClearancePayload & { projectId: string }): StartClearanceInput => p;
void _assignableToStartClearanceInput;

/**
 * The mutation block. `stationFrom` / `stationTo` are OMITTED when the user
 * left the range untouched — the server then means "the whole alignment",
 * which stays true if the alignment is later re-tessellated a millimetre
 * longer. Sending a computed 0 and `length` would freeze today's numbers.
 */
export const clearancePayload = (values: ClearanceFormValues, derived: ClearanceDerived): ClearancePayload => {
  const alignment = derived.alignment;
  if (!alignment) throw new Error('clearancePayload called without an alignment');
  const fromTouched = values.stationFromText.trim().length > 0;
  const toTouched = values.stationToText.trim().length > 0;
  return {
    pointcloudLayerId: values.pointcloudLayerId,
    alignment: toAlignmentInput(alignment),
    profile: {
      name: values.profileName.trim() || 'Profile',
      sourceAttribution: values.profileSourceAttribution,
      vertices: validateProfileVertices(values.profileVertices),
    },
    verticalOffsetM: values.verticalOffsetM,
    stationInterval: values.stationInterval,
    ...(fromTouched ? { stationFrom: derived.fromInternal } : {}),
    ...(toTouched ? { stationTo: derived.toInternal } : {}),
    ...(values.name.trim() ? { name: values.name.trim() } : {}),
  };
};

/** Display-station label for a derived range, for the dialog's scope line. */
export const clearanceRangeLabel = (derived: ClearanceDerived): string | null => {
  if (!derived.alignment) return null;
  const a = derived.alignment;
  return `${internalToDisplay(a, derived.fromInternal).toFixed(2)} – ${internalToDisplay(a, derived.toInternal).toFixed(
    2,
  )}`;
};
